import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Fragment } from "react";
import { TrendingUp } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, num } from "@/lib/formatters";
import OversiktCompareCharts from "./OversiktCompareCharts";
import BoligCompareCharts from "./BoligCompareCharts";
import HusholdningCompareChart from "./HusholdningCompareChart";
import ArbeidCompareChart from "./ArbeidCompareChart";
import PendlingCompareChart from "./PendlingCompareChart";
import NaeringCompareChart from "./NaeringCompareChart";
import AlderCompareChart from "./AlderCompareChart";
import KorttidCompareChart from "./KorttidCompareChart";
import ArbeidskapitalCompareChart from "./ArbeidskapitalCompareChart";
import RangeringCompareChart from "./RangeringCompareChart";
import UlikskapCompareChart from "./UlikskapCompareChart";
import TjenesterCompareChart from "./TjenesterCompareChart";
import EiendomCompareChart from "./EiendomCompareChart";
import FinanskostnadCompareChart from "./FinanskostnadCompareChart";

export const COLORS = ["#103766", "#3F8A86", "#D35E4E", "#E8A34A", "#8B5CF6"];
export const FYLKE_COLOR = "#9CA3AF"; // Gray for fylke average

// All comparison charts (finance, balance, per-capita, demographics, bolig) for the selected kommuner
const ComparisonCharts = ({ comparisonData, demographicsData, fylkeAverageData, showFylkeAverage, oversiktData, boligData, arbeidData, pendlingData, alderData, korttidData, arbeidskapitalData, rangeringCompareData, ulikskapCompareData, tjenesterCompareData, eiendomCompareData, finanskostnadCompareData }) => {
  const prepareIncomeChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          dataPoint[`${kommune.name}_inntekt`] = yearData?.inntekt;
          dataPoint[`${kommune.name}_kostnad`] = yearData?.kostnad;
        });
        return dataPoint;
      });
  };

  const prepareDebtChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          // Total gjeld = kortsiktig gjeld (gjeld) + langsiktig lån (laan)
          const totalGjeld = (yearData?.gjeld || 0) + (yearData?.laan || 0);
          dataPoint[kommune.name] = totalGjeld > 0 ? totalGjeld : null;
        });
        return dataPoint;
      });
  };

  // Prepare expenses chart data (driftsutgifter over tid)
  const prepareExpensesChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          dataPoint[kommune.name] = yearData?.kostnad || null;
        });
        return dataPoint;
      });
  };

  // Prepare margin chart data (driftsmargin over tid) - includes fylke average
  const prepareMarginChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });
    if (fylkeAverageData?.data) {
      fylkeAverageData.data.forEach((d) => years.add(d.year));
    }

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          if (yearData?.inntekt && yearData?.kostnad) {
            const margin = ((yearData.inntekt - yearData.kostnad) / yearData.inntekt) * 100;
            dataPoint[kommune.name] = parseFloat(margin.toFixed(2));
          } else {
            dataPoint[kommune.name] = null;
          }
        });
        // Add fylke average margin
        if (showFylkeAverage && fylkeAverageData?.data) {
          const avgData = fylkeAverageData.data.find((d) => d.year === year);
          if (avgData?.inntekt && avgData?.kostnad) {
            const avgMargin = ((avgData.inntekt - avgData.kostnad) / avgData.inntekt) * 100;
            dataPoint[fylkeAverageData.name] = parseFloat(avgMargin.toFixed(2));
          }
        }
        return dataPoint;
      });
  };

  // Prepare assets chart data (eiendeler over tid)
  const prepareAssetsChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          dataPoint[kommune.name] = yearData?.anleggsmidler || null;
        });
        return dataPoint;
      });
  };

  // Prepare debt ratio chart data (gjeldsgrad over tid) - includes fylke average
  const prepareDebtRatioChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });
    if (fylkeAverageData?.data) {
      fylkeAverageData.data.forEach((d) => years.add(d.year));
    }

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          const totalGjeld = (yearData?.gjeld || 0) + (yearData?.laan || 0);
          if (yearData?.anleggsmidler && totalGjeld > 0) {
            const ratio = (totalGjeld / yearData.anleggsmidler) * 100;
            dataPoint[kommune.name] = parseFloat(ratio.toFixed(2));
          } else {
            dataPoint[kommune.name] = null;
          }
        });
        // Add fylke average debt ratio
        if (showFylkeAverage && fylkeAverageData?.data) {
          const avgData = fylkeAverageData.data.find((d) => d.year === year);
          const avgTotalGjeld = (avgData?.gjeld || 0) + (avgData?.laan || 0);
          if (avgData?.anleggsmidler && avgTotalGjeld > 0) {
            const avgRatio = (avgTotalGjeld / avgData.anleggsmidler) * 100;
            dataPoint[fylkeAverageData.name] = parseFloat(avgRatio.toFixed(2));
          }
        }
        return dataPoint;
      });
  };

  // Prepare property tax chart data (eiendomsskatt)
  const preparePropertyTaxChartData = () => {
    return Object.entries(comparisonData).map(([code, kommune], idx) => {
      // Calculate average property tax across all years
      const taxData = kommune.data?.filter(d => d.eiendomsskatt_total != null && d.eiendomsskatt_total > 0) || [];
      const avgTax = taxData.length > 0 
        ? taxData.reduce((sum, d) => sum + (d.eiendomsskatt_total || 0), 0) / taxData.length
        : 0;
      
      return {
        name: kommune.name,
        code: code,
        avgTax: Math.round(avgTax),
        color: COLORS[idx % COLORS.length]
      };
    }).filter(d => d.avgTax > 0);
  };

  // Prepare per capita income/expenses chart data
  const preparePerCapitaIncomeChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          if (yearData?.population && yearData.population > 0) {
            if (yearData.inntekt) {
              dataPoint[`${kommune.name}_inntekt`] = Math.round((yearData.inntekt * 1000) / yearData.population);
            }
            if (yearData.kostnad) {
              dataPoint[`${kommune.name}_utgift`] = Math.round((yearData.kostnad * 1000) / yearData.population);
            }
          }
        });
        return dataPoint;
      });
  };

  // Prepare per capita debt/assets chart data
  const preparePerCapitaBalanceChartData = () => {
    const years = new Set();
    Object.values(comparisonData).forEach((kommune) => {
      kommune.data?.forEach((d) => years.add(d.year));
    });

    return Array.from(years)
      .sort()
      .map((year) => {
        const dataPoint = { year };
        Object.entries(comparisonData).forEach(([code, kommune]) => {
          const yearData = kommune.data?.find((d) => d.year === year);
          if (yearData?.population && yearData.population > 0) {
            const totalGjeld = (yearData?.gjeld || 0) + (yearData?.laan || 0);
            if (totalGjeld > 0) {
              dataPoint[`${kommune.name}_gjeld`] = Math.round((totalGjeld * 1000) / yearData.population);
            }
            if (yearData.anleggsmidler) {
              dataPoint[`${kommune.name}_eiendeler`] = Math.round((yearData.anleggsmidler * 1000) / yearData.population);
            }
          }
        });
        return dataPoint;
      });
  };

  // Calculate latest metrics for comparison table
  const prepareMedianIncomeChartData = () => {
    return Object.entries(comparisonData).map(([code, kommune], idx) => {
      const demographics = demographicsData[code];
      return {
        name: kommune.name,
        median_income_person: demographics?.median_income_person || null,
        median_income_household: demographics?.median_income || null,
        color: COLORS[idx % COLORS.length],
      };
    }).filter(d => d.median_income_person !== null);
  };

  const incomeChartData = prepareIncomeChartData();
  const debtChartData = prepareDebtChartData();
  const expensesChartData = prepareExpensesChartData();
  const marginChartData = prepareMarginChartData();
  const assetsChartData = prepareAssetsChartData();
  const debtRatioChartData = prepareDebtRatioChartData();
  const propertyTaxChartData = preparePropertyTaxChartData();
  const perCapitaIncomeChartData = preparePerCapitaIncomeChartData();
  const perCapitaBalanceChartData = preparePerCapitaBalanceChartData();
  const medianIncomeChartData = prepareMedianIncomeChartData();

  return (
    <>
            {/* Charts - Grouped by category */}
            
            {/* === RESULTATDATA (Driftsresultat) === */}
            <h3 className="text-lg font-semibold text-[#1F2A36] mt-6 mb-4">Resultatdata</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Income Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Driftsinntekter over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={incomeChartData}>
                        {endringsLinjer(incomeChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`}
                        />
                        <Tooltip
                          formatter={(value) => formatNumber(value)}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Line
                            key={code}
                            type="monotone"
                            dataKey={`${kommune.name}_inntekt`}
                            name={kommune.name}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ fill: COLORS[idx % COLORS.length] }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Expenses Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Driftsutgifter over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={expensesChartData}>
                        {endringsLinjer(expensesChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`}
                        />
                        <Tooltip
                          formatter={(value) => formatNumber(value)}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Line
                            key={code}
                            type="monotone"
                            dataKey={kommune.name}
                            name={kommune.name}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ fill: COLORS[idx % COLORS.length] }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Per Capita Income/Expenses Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Driftsinntekter og driftsutgifter pr innbyggjar (kr)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={perCapitaIncomeChartData}>
                        {endringsLinjer(perCapitaIncomeChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          formatter={(value) => `${formatNumber(value)} kr`}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Fragment key={code}>
                            <Line
                              key={`${code}_inntekt`}
                              type="monotone"
                              dataKey={`${kommune.name}_inntekt`}
                              name={`${kommune.name} (inntekt)`}
                              stroke={COLORS[idx % COLORS.length]}
                              strokeWidth={2}
                              dot={{ fill: COLORS[idx % COLORS.length] }}
                            />
                            <Line
                              key={`${code}_utgift`}
                              type="monotone"
                              dataKey={`${kommune.name}_utgift`}
                              name={`${kommune.name} (utgift)`}
                              stroke={COLORS[idx % COLORS.length]}
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: COLORS[idx % COLORS.length] }}
                            />
                          </Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Margin Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Driftsmargin over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={marginChartData}>
                        {endringsLinjer(marginChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${num(v)}%`}
                          domain={['auto', 'auto']}
                        />
                        <Tooltip
                          formatter={(value) => `${num(value)}%`}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Line
                            key={code}
                            type="monotone"
                            dataKey={kommune.name}
                            name={kommune.name}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ fill: COLORS[idx % COLORS.length] }}
                          />
                        ))}
                        {showFylkeAverage && fylkeAverageData && (
                          <Line
                            type="monotone"
                            dataKey={fylkeAverageData.name}
                            name={fylkeAverageData.name}
                            stroke={FYLKE_COLOR}
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{ fill: FYLKE_COLOR }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* === BALANSEDATA === */}
            <h3 className="text-lg font-semibold text-[#1F2A36] mt-8 mb-4">Balansedata</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Debt Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Total gjeld over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={debtChartData}>
                        {endringsLinjer(debtChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`}
                        />
                        <Tooltip
                          formatter={(value) => formatNumber(value)}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Bar
                            key={code}
                            dataKey={kommune.name}
                            fill={COLORS[idx % COLORS.length]}
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Assets Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Eigedelar over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={assetsChartData}>
                        {endringsLinjer(assetsChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`}
                        />
                        <Tooltip
                          formatter={(value) => formatNumber(value)}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Bar
                            key={code}
                            dataKey={kommune.name}
                            fill={COLORS[idx % COLORS.length]}
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Per Capita Balance Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Gjeld og eigedelar pr innbyggjar (kr)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={perCapitaBalanceChartData}>
                        {endringsLinjer(perCapitaBalanceChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          formatter={(value) => `${formatNumber(value)} kr`}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Fragment key={code}>
                            <Line
                              key={`${code}_eiendeler`}
                              type="monotone"
                              dataKey={`${kommune.name}_eiendeler`}
                              name={`${kommune.name} (eigedelar)`}
                              stroke={COLORS[idx % COLORS.length]}
                              strokeWidth={2}
                              dot={{ fill: COLORS[idx % COLORS.length] }}
                            />
                            <Line
                              key={`${code}_gjeld`}
                              type="monotone"
                              dataKey={`${kommune.name}_gjeld`}
                              name={`${kommune.name} (gjeld)`}
                              stroke={COLORS[idx % COLORS.length]}
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: COLORS[idx % COLORS.length] }}
                            />
                          </Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Debt Ratio Chart */}
              <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36]">
                    Gjeldsgrad over tid
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={debtRatioChartData}>
                        {endringsLinjer(debtRatioChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis
                          stroke="#8AA4BF"
                          fontSize={12}
                          tickFormatter={(v) => `${num(v)}%`}
                          domain={[0, 'auto']}
                        />
                        <Tooltip
                          formatter={(value) => `${num(value)}%`}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        {Object.entries(comparisonData).map(([code, kommune], idx) => (
                          <Line
                            key={code}
                            type="monotone"
                            dataKey={kommune.name}
                            name={kommune.name}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ fill: COLORS[idx % COLORS.length] }}
                          />
                        ))}
                        {showFylkeAverage && fylkeAverageData && (
                          <Line
                            type="monotone"
                            dataKey={fylkeAverageData.name}
                            name={fylkeAverageData.name}
                            stroke={FYLKE_COLOR}
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{ fill: FYLKE_COLOR }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <ArbeidskapitalCompareChart arbeidskapitalData={arbeidskapitalData} colors={COLORS} />

              <div className="lg:col-span-2">
                <RangeringCompareChart data={rangeringCompareData} />
              </div>

              <div className="lg:col-span-2">
                <UlikskapCompareChart data={ulikskapCompareData} colors={COLORS} />
                <TjenesterCompareChart data={tjenesterCompareData} colors={COLORS} />
                <EiendomCompareChart data={eiendomCompareData} colors={COLORS} />
                <FinanskostnadCompareChart data={finanskostnadCompareData} colors={COLORS} />
              </div>

              {/* Property Tax Chart */}
              {propertyTaxChartData.length > 0 && (
                <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2">
                  <CardHeader className="border-b border-[#E6E9F0] pb-4">
                    <CardTitle className="text-lg font-medium text-[#1F2A36]">
                      Gjennomsnittleg årleg eigedomsskatt (1000 kr)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={propertyTaxChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                          <XAxis 
                            type="number" 
                            stroke="#8AA4BF" 
                            fontSize={12}
                            tickFormatter={(v) => formatNumber(v)}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            stroke="#8AA4BF" 
                            fontSize={12}
                            width={150}
                          />
                          <Tooltip
                            formatter={(value) => `${formatNumber(value)} tusen kr`}
                            contentStyle={{
                              background: "white",
                              border: "1px solid #E6E9F0",
                              borderRadius: "8px",
                            }}
                          />
                          <Bar 
                            dataKey="avgTax" 
                            name="Eigedomsskatt"
                            radius={[0, 4, 4, 0]}
                          >
                            {propertyTaxChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              <AlderCompareChart alderData={alderData} colors={COLORS} />

              {/* Median Income Chart */}
              {medianIncomeChartData.length > 0 && (
                <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                  <CardHeader className="border-b border-[#E6E9F0]">
                    <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#3F8A86]" />
                      Median bruttoinntekt per innbyggjar
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={medianIncomeChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                          <XAxis 
                            type="number" 
                            stroke="#8AA4BF" 
                            fontSize={12}
                            tickFormatter={(v) => formatNumber(v)}
                          />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            stroke="#8AA4BF" 
                            fontSize={12}
                            width={150}
                          />
                          <Tooltip
                            formatter={(value) => `${formatNumber(value)} kr`}
                            contentStyle={{
                              background: "white",
                              border: "1px solid #E6E9F0",
                              borderRadius: "8px",
                            }}
                          />
                          <Bar 
                            dataKey="median_income_person" 
                            name="Median bruttoinntekt"
                            radius={[0, 4, 4, 0]}
                          >
                            {medianIncomeChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-xs text-[#8AA4BF] mt-4 text-center">
                      Kjelde: SSB inntektsstatistikk 2023
                    </p>
                  </CardContent>
                </Card>
              )}
              <OversiktCompareCharts oversiktData={oversiktData} colors={COLORS} />
              <BoligCompareCharts boligData={boligData} colors={COLORS} />
              <HusholdningCompareChart boligData={boligData} colors={COLORS} />
              <ArbeidCompareChart arbeidData={arbeidData} />
              <PendlingCompareChart pendlingData={pendlingData} />
              <NaeringCompareChart pendlingData={pendlingData} colors={COLORS} />
              <KorttidCompareChart korttidData={korttidData} colors={COLORS} />
            </div>
    </>
  );
};

export default ComparisonCharts;
